import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Tag } from '@/components/ui/tag';
import trashIcon from '@/assets/images/trash-bin-trash.svg';
import {
  ADDITIONAL_QUESTION_RESPONSE_TYPES,
  getResponseTypeLabel,
  MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE,
  responseTypeRequiresOptions,
  type AdditionalQuestionResponseType,
} from './additionalQuestionConfig';

interface QuestionnaireAdditionalQuestionsPanelProps {
  questionCount: number;
  existingQuestions?: Array<{
    id: string;
    code: string;
    questionText: string;
    typeLabel: string;
    chips: string[];
    required: boolean;
  }>;
  questionText: string;
  required: boolean;
  responseType: AdditionalQuestionResponseType;
  optionLines: string;
  onDeleteExistingQuestion?: (questionId: string) => void;
  deletingQuestionId?: string;
  canDeleteQuestions?: boolean;
  onQuestionTextChange: (value: string) => void;
  onRequiredChange: (checked: boolean) => void;
  onResponseTypeChange: (value: AdditionalQuestionResponseType) => void;
  onOptionLinesChange: (value: string) => void;
  onSave: () => void;
  onOpenCopyFromQuestionnaire: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;
  disableAddQuestionBox?: boolean;
}

function getQuestionCountLabel(questionCount: number) {
  return `${questionCount} ${questionCount === 1 ? 'question' : 'questions'}`;
}

const REQUIRED_TOOLTIP_MESSAGE = 'This makes the question mandatory for clients.';

const REQUIRED_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 260,
      bgcolor: '#D9F6FF',
      color: '#101828',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      fontSize: { xs: '12px', sm: '13px' },
      lineHeight: { xs: '16px', sm: '20px' },
      textAlign: 'center',
      px: { xs: 1.5, sm: 2 },
      py: { xs: 1, sm: 1.25 },
      borderRadius: '6px',
      boxShadow: '0px 12px 32px rgba(16, 24, 40, 0.16)',
    },
  },
  arrow: {
    sx: {
      color: '#D9F6FF',
    },
  },
} as const;

export function QuestionnaireAdditionalQuestionsPanel({
  questionCount,
  existingQuestions = [],
  questionText,
  required,
  responseType,
  optionLines,
  onDeleteExistingQuestion,
  deletingQuestionId,
  canDeleteQuestions = true,
  onQuestionTextChange,
  onRequiredChange,
  onResponseTypeChange,
  onOptionLinesChange,
  onSave,
  onOpenCopyFromQuestionnaire,
  isSaving = false,
  saveDisabled = false,
  disableAddQuestionBox = false,
}: QuestionnaireAdditionalQuestionsPanelProps) {
  const needsOptions = responseTypeRequiresOptions(responseType);
  const hasPendingQuestionDraft =
    questionText.trim().length > 0 ||
    optionLines.trim().length > 0 ||
    required ||
    responseType !== 'text';
  const customQuestionsLabelColor =
    disableAddQuestionBox || !hasPendingQuestionDraft ? '#98a2b3' : '#344054';

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '18px',
        border: '1px solid #dbe6eb',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2.25,
          py: 1.8,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#e7efff',
              color: '#4f8cff',
              flexShrink: 0,
            }}
          >
            <TipsAndUpdatesRoundedIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.41px',
              color: '#4f8cff',
            }}
          >
            Additional Questions
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: 0,
              color: '#98a2b3',
            }}
          >
            {getQuestionCountLabel(questionCount)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid #eef2f6',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
        }}
      >
          {existingQuestions.length > 0 ? (
            <Box sx={{ mb: 2, mx: { xs: -2, md: -3 } }}>
              {existingQuestions.map((question) => {
                const deleteDisabled = deletingQuestionId === question.id;

                return (
                  <Box
                    key={question.id}
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 1.5,
                      borderBottom: '1px solid #eef2f6',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '84px minmax(0, 1fr)' },
                        gap: 1.5,
                        alignItems: 'start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '16px',
                          letterSpacing: 0,
                          color: '#98a2b3',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {question.code}
                      </Typography>

                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', md: 'row' },
                              alignItems: { xs: 'flex-start', md: 'center' },
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '-0.15px',
                                color: '#0f172a',
                              }}
                            >
                              {question.questionText}
                              {question.required ? ' *' : ''}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Arial, sans-serif',
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#98a2b3',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              • {question.typeLabel}
                            </Typography>
                          </Box>

                          {canDeleteQuestions ? (
                            <IconButton
                              size="small"
                              disabled={deleteDisabled}
                              onClick={() => onDeleteExistingQuestion?.(question.id)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#f97066',
                                flexShrink: 0,
                                '&:hover': {
                                  bgcolor: 'rgba(249, 112, 102, 0.08)',
                                },
                              }}
                            >
                              <Box component="img" src={trashIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                            </IconButton>
                          ) : null}
                        </Box>

                        {question.chips.length > 0 ? (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {question.chips.map((chip) => (
                              <Tag
                                key={`${question.id}-${chip}`}
                                variant="neutral"
                                size="xs"
                                className="h-[22px] min-w-[62.68px] rounded-[10px] border-b border-b-[#E8E2D5] bg-[#F6EDDC] font-[Inter,sans-serif] text-xs font-semibold leading-4 text-[#7C6F52]"
                              >
                                {chip}
                              </Tag>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#667085',
            }}
          >
            Question Text
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={2}
            value={questionText}
            onChange={(event) => onQuestionTextChange(event.target.value)}
            placeholder="Write your question here"
            disabled={disableAddQuestionBox}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#d0d5dd' },
              },
            }}
          />

          <Typography
            sx={{
              mt: 1,
              fontFamily: 'Arial, sans-serif',
              fontSize: '12px',
              lineHeight: '16px',
              color: '#98a2b3',
            }}
          >
            The saved answer format follows the selected response type.
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#667085',
                }}
              >
                Response Type:
              </Typography>
              <TextField
                select
                size="small"
                value={responseType}
                disabled={disableAddQuestionBox}
                onChange={(event) => onResponseTypeChange(event.target.value as AdditionalQuestionResponseType)}
                sx={{
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                    borderRadius: '999px',
                    bgcolor: '#f2f4f7',
                    '& fieldset': { borderColor: 'transparent' },
                  },
                  '& .MuiSelect-select': {
                    py: 1,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#667085',
                  },
                }}
              >
                {ADDITIONAL_QUESTION_RESPONSE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Tooltip title={REQUIRED_TOOLTIP_MESSAGE} placement="top" arrow slotProps={REQUIRED_TOOLTIP_SLOT_PROPS}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={required}
                      disabled={disableAddQuestionBox}
                      onChange={(_, checked) => onRequiredChange(checked)}
                      sx={{ color: '#98a2b3', '&.Mui-checked': { color: '#25a8da' } }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#667085',
                      }}
                    >
                      Required
                    </Typography>
                  }
                  sx={{ m: 0, gap: 0.5 }}
                />
              </Box>
            </Tooltip>
          </Box>

          {needsOptions && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#667085',
                }}
              >
                Options
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={optionLines}
                disabled={disableAddQuestionBox}
                onChange={(event) => onOptionLinesChange(event.target.value)}
                placeholder={`Option 1\nOption 2`}
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#d0d5dd' },
                  },
                }}
              />
              <Typography
                sx={{
                  mt: 1,
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#98a2b3',
                }}
              >
                Add one option per line for {getResponseTypeLabel(responseType).toLowerCase()} questions.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
              gap: 1.25,
            }}
          >
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saveDisabled || disableAddQuestionBox}
              sx={{
                minWidth: { sm: 122 },
                borderRadius: '10px',
                bgcolor: '#25a8da',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
              }}
            >
              {isSaving ? <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={onOpenCopyFromQuestionnaire}
              sx={{
                borderRadius: '10px',
                borderColor: '#d0d5dd',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                '&:hover': { borderColor: '#bfc7d0', bgcolor: '#f8fafc' },
              }}
            >
              Copy from Questionnaire
            </Button>
          </Box>

          <Button
            variant="outlined"
            endIcon={<AddRoundedIcon />}
            disabled={disableAddQuestionBox || !hasPendingQuestionDraft}
            sx={{
              mt: 2.25,
              width: '100%',
              borderRadius: '10px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              '& .MuiButton-endIcon': {
                color: '#344054',
                ml: 1,
                mr: 0,
              },
              '&:hover': { borderColor: '#bfc7d0', bgcolor: '#D9F6FF' },
              '&.Mui-disabled': {
                opacity: 1,
                borderColor: '#d0d5dd',
                color: '#344054',
                bgcolor: 'transparent',
              },
            }}
          >
            <Box component="span" sx={{ color: customQuestionsLabelColor }}>
              Custom Questions
            </Box>
          </Button>

          {disableAddQuestionBox ? (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 1, sm: 1.15 },
                borderRadius: '12px',
                border: '1.5px solid #f2c94c',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  bgcolor: '#fef0c7',
                  color: '#f79009',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '12px', sm: '13px' },
                    lineHeight: { xs: '16px', sm: '18px' },
                    color: '#101828',
                  }}
                >
                  Maximum {MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE} custom questions reached
                </Typography>
                <Typography
                  sx={{
                    mt: 0.15,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '12px', sm: '12px' },
                    lineHeight: { xs: '14px', sm: '16px' },
                    color: '#344054',
                  }}
                >
                  Remove a custom question above to free up a slot.
                </Typography>
              </Box>
            </Box>
          ) : null}
      </Box>
    </Paper>
  );
}
